import * as yup from "yup";
import React, {useEffect} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useFormik} from "formik";
import {Col, Form, Modal, ModalBody, ModalHeader, Row} from "reactstrap";
import {ORDER_STATUSES} from "../../TrafficOrders/constants/order";
import {CommonOrderService} from "../../../../services/common_order.service";
import Select from "../../../../components/Common/Select";

interface ChangeStatusCommonOrderProps {
    commonOrderId: number;
    date: string;

    isOpen: boolean;
    toggle: () => void;
}

interface StatusOrder {
    status: number;
}

const ChangeStatusCommonOrder: React.FC<ChangeStatusCommonOrderProps> = ({
                                                                             isOpen,
                                                                             toggle,
                                                                             commonOrderId,
                                                                             date
                                                                         }) => {
    const queryClient = useQueryClient();

    const {data: statusCommonOrder, isFetching} = useQuery({
        queryKey: ["keyStatusCommonOrder", commonOrderId, date],
        queryFn: () => CommonOrderService.getStatusCommonOrder(commonOrderId, date),
        retry: false
    });

    const {
        mutate: updateStatus,
        isPending: isPendingUpdate,
        isSuccess: isSuccess
    } = useMutation({
        mutationFn: (data: StatusOrder) => CommonOrderService.updateStatusCommonOrder(commonOrderId, data),
        mutationKey: ["keyUpdateStatusCommonOrder", commonOrderId],
    });

    const validationSchema: yup.Schema<{ status: number }> = yup.object().shape({
        status: yup.number()
            .min(1, "min 1")
            .max(3, "max 3")
            .required("required")
    });

    const initialValues: { status: number } = {
        status: statusCommonOrder?.status || 0,
    }

    const handleSubmitForm = (value: { status: number }) => {
        updateStatus(value)
    }

    const formik = useFormik({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema,
        validateOnMount: true,
        enableReinitialize: true,
    });

    const {submitForm, setFieldValue, values, touched, errors, isValid} = formik;

    useEffect(() => {
        if (isSuccess) {
            toggle();
            queryClient.invalidateQueries({queryKey: ['keyCommonOrders']});
        }
    }, [isSuccess]);

    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle}>Change Status Order {commonOrderId}</ModalHeader>
            <ModalBody>
                <Form>
                    <Row>
                        <Col xs={12}>
                            <div className="mb-4">
                                <label>Select status</label>
                                <Select
                                    name="status"
                                    options={ORDER_STATUSES
                                        .filter(({value}) => ![4, 5].includes(value))
                                        .map(({value, label}) => ({value, label})) || []}
                                    placeholder="Select status..."
                                    isClearable
                                    isDisabled={isFetching}
                                    isLoading={isFetching}
                                    onChange={(option: any) => setFieldValue("status", option ? option.value : null)}
                                    value={ORDER_STATUSES?.find((i: any) => i.value === values.status) || null}
                                />
                                {touched.status && errors.status && (
                                    <div className="text-danger">{errors.status as string}</div>
                                )}
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="text-end">
                            <button
                                type="button"
                                className="btn btn-success save-user"
                                onClick={submitForm}
                                disabled={!isValid || isPendingUpdate}
                            >
                                Save
                            </button>
                        </Col>
                    </Row>
                </Form>
            </ModalBody>
        </Modal>
    );
};

export default ChangeStatusCommonOrder;